import { ReactNode, useState, useContext } from "react";
import {
  Box,
  Typography,
  ButtonGroup,
  IconButton,
  MenuItem,
  ListItem,
} from "@mui/material";
import { Button } from "components/base/Button";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Settings,
} from "lucide-react";
import { View } from "react-big-calendar";
import useIsMobile from "components/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import { StyledMenu } from "components/common/DropdownMenu";
import { StatusContext } from "../context/StatusContextProvider";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";
import Select from "../common/Select";
import { SubmenuMenuOption } from "../common/PopperMenu";
import { Timezones, TimezoneAbbreviations } from "../common/Timezones";

export interface BaseToolbarProps {
  onNavigate: (action: "PREV" | "NEXT" | "TODAY") => void;
  label: string;
  onView: (view: View) => void;
  view: View;
}
export interface CustomToolbarProps extends BaseToolbarProps {
  minimalHeader?: boolean;
  filtersDelegate?: ReactNode | null;
  handleNewEvent?: () => void;
  handleRefresh?: () => void;
  settingsUrl: string;
  onSearchClick?: () => void;
  onDualTimezoneChange?: (dualEnabled: boolean, secondaryTz?: string) => void;
  currentSecondaryTimezone?: string;
  dualTimezoneEnabled?: boolean;
}

export default ({
  onNavigate,
  label,
  onView,
  view,
  minimalHeader,
  filtersDelegate,
  handleNewEvent,
  handleRefresh,
  settingsUrl,
  onSearchClick,
  onDualTimezoneChange,
  currentSecondaryTimezone,
  dualTimezoneEnabled,
}: CustomToolbarProps) => {
  const { datebookUnreadCount } = useContext(StatusContext);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const timezoneOptions: { label: string; value: string }[] = [];

  for (const tz of Timezones) {
    timezoneOptions.push({
      label: TimezoneAbbreviations[tz] || tz.split("/").pop() || tz,
      value: tz,
    });

    if (tz === "Pacific/Honolulu") {
      timezoneOptions.push({ label: "divider", value: "divider" });
    }
  }

  const handleSecondaryTimezoneChange = (timezone: string) => {
    if (onDualTimezoneChange) {
      if (timezone === "disable") {
        onDualTimezoneChange(false, "");
      } else {
        onDualTimezoneChange(true, timezone);
      }
    }
  };

  const getSubmenuOptions = () => {
    const options = [];
    if (dualTimezoneEnabled) {
      options.push({ label: "Turn off", value: "disable" });
    }
    options.push(...timezoneOptions);
    return options;
  };

  const modeSelection = minimalHeader ? (
    <Select
      value={view}
      onChange={(e) => {
        const val = e.target.value as View;
        onView(val);
      }}
      sx={{
        ".MuiInputBase-input": {
          paddingRight: "5px !important",
          minWidth: "36px",
        },
      }}
    >
      <MenuItem value="day">
        <ListItem
          component="div"
          disablePadding
          sx={(theme) => ({ color: theme.palette.text.primary })}
        >
          Day
        </ListItem>
      </MenuItem>
      <MenuItem value="week">
        <ListItem
          component="div"
          disablePadding
          sx={(theme) => ({ color: theme.palette.text.primary })}
        >
          Week
        </ListItem>
      </MenuItem>
      <MenuItem value="month">
        <ListItem
          component="div"
          disablePadding
          sx={(theme) => ({ color: theme.palette.text.primary })}
        >
          Month
        </ListItem>
      </MenuItem>
      <MenuItem value="agenda">
        <ListItem
          component="div"
          disablePadding
          sx={(theme) => ({ color: theme.palette.text.primary })}
        >
          List
        </ListItem>
      </MenuItem>
    </Select>
  ) : (
    <ButtonGroup fullWidth sx={{ height: "48px" }}>
      <Button
        onClick={() => onView("day")}
        variant={view === "day" ? "buttonGroupSelected" : "buttonGroup"}
      >
        Day
      </Button>
      <Button
        onClick={() => onView("week")}
        variant={view === "week" ? "buttonGroupSelected" : "buttonGroup"}
      >
        Week
      </Button>
      <Button
        onClick={() => onView("month")}
        variant={view === "month" ? "buttonGroupSelected" : "buttonGroup"}
      >
        Month
      </Button>
      <Button
        onClick={() => onView("agenda")}
        variant={view === "agenda" ? "buttonGroupSelected" : "buttonGroup"}
      >
        List
        {datebookUnreadCount > 0 && (
          <Box>
            {/* Do not remove wrapping Box/div, it prevents the svg from shrinking when screensizes are small */}
            <EllipseIcon height={8} width={8} style={{ marginLeft: "5px" }} />
          </Box>
        )}
      </Button>
    </ButtonGroup>
  );

  const addEvent = handleNewEvent && (
    <IconButton
      onClick={handleNewEvent}
      sx={{
        borderRadius: "50px",
        border: "2px solid #d4d4d4",
        padding: "9px",
        borderWidth: "1px",
      }}
    >
      <Plus size={20} />
    </IconButton>
  );

  if (isMobile) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="12px"
        alignItems="center"
        padding={minimalHeader ? "0px 0px" : "8px 0px"}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent={minimalHeader ? "space-between" : "center"}
          width="100%"
          alignItems="center"
          gap="12px"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2Serif">{label}</Typography>
            <IconButton onClick={handleClick}>
              <Settings color="#6B6E7B" strokeWidth="1.5" />
            </IconButton>
            <StyledMenu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuItem
                disabled={false}
                onClick={() => {
                  setAnchorEl(null);
                  navigate(settingsUrl);
                }}
                disableRipple
                sx={{
                  gap: "12px",
                }}
              >
                Settings
              </MenuItem>
              <MenuItem
                disabled={false}
                onClick={() => {
                  setAnchorEl(null);
                  handleClose();
                }}
                disableRipple
                sx={{
                  gap: "12px",
                }}
              >
                Refresh
              </MenuItem>
              {onDualTimezoneChange && (
                <SubmenuMenuOption
                  sx={{
                    padding: "8px 20px",
                  }}
                  title="Dual timezone"
                  onClick={handleSecondaryTimezoneChange}
                  submenuOptions={getSubmenuOptions()}
                  currentValue={
                    dualTimezoneEnabled ? currentSecondaryTimezone : undefined
                  }
                  showCheckmark={true}
                />
              )}
            </StyledMenu>
          </Box>
          {minimalHeader && modeSelection}
        </Box>
        {!minimalHeader && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent={!minimalHeader ? "space-between" : "center"}
            width="100%"
            alignItems="center"
            gap="12px"
          >
            {modeSelection}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMobile ? "0" : "12px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
            }}
          >
            <Button
              variant={isMobile ? "text" : "outlined"}
              borderColor="border"
              size="small"
              color="text.primary"
              onClick={() => onNavigate("TODAY")}
            >
              Today
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => onNavigate("PREV")}
              sx={{ minWidth: minimalHeader ? "32px" : "48px" }}
            >
              <ChevronLeft color="#6B6E7B" />
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => onNavigate("NEXT")}
              sx={minimalHeader ? { minWidth: "32px" } : undefined}
            >
              <ChevronRight color="#6B6E7B" />
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {filtersDelegate}
            {onSearchClick && (
              <IconButton onClick={onSearchClick}>
                <Search color="#6B6E7B" strokeWidth="1.5" />
              </IconButton>
            )}
            {addEvent}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "12px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {addEvent}
          {/* Forward and back */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant={minimalHeader ? "text" : "outlined"}
              borderColor="border"
              size="small"
              color="text.primary"
              onClick={() => onNavigate("TODAY")}
            >
              Today
            </Button>

            <Button
              variant="text"
              onClick={() => onNavigate("PREV")}
              sx={minimalHeader ? { minWidth: "32px" } : undefined}
            >
              <ChevronLeft color="#6B6E7B" />
            </Button>
            <Button
              variant="text"
              onClick={() => onNavigate("NEXT")}
              sx={minimalHeader ? { minWidth: "32px" } : undefined}
            >
              <ChevronRight color="#6B6E7B" />
            </Button>
          </Box>
        </Box>

        {/* Date range */}
        <Typography variant="h2Serif">{label}</Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            gap: "12px",
          }}
        >
          {filtersDelegate}
          {onSearchClick && (
            <IconButton onClick={onSearchClick}>
              <Search color="#6B6E7B" strokeWidth="1.5" />
            </IconButton>
          )}
          <IconButton onClick={handleClick}>
            <Settings color="#6B6E7B" strokeWidth="1.5" />
          </IconButton>
          <StyledMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <MenuItem
              disabled={false}
              onClick={() => {
                setAnchorEl(null);
                navigate(settingsUrl);
              }}
              disableRipple
              sx={{
                gap: "12px",
              }}
            >
              Settings
            </MenuItem>
            {handleRefresh && (
              <MenuItem
                disabled={false}
                onClick={() => {
                  handleRefresh();
                  setAnchorEl(null);
                  handleClose();
                }}
                disableRipple
                sx={{
                  gap: "12px",
                }}
              >
                Refresh
              </MenuItem>
            )}
            {onDualTimezoneChange && (
              <SubmenuMenuOption
                sx={{
                  padding: "8px 20px",
                }}
                title="Dual timezone"
                onClick={handleSecondaryTimezoneChange}
                submenuOptions={getSubmenuOptions()}
                currentValue={
                  dualTimezoneEnabled ? currentSecondaryTimezone : undefined
                }
                showCheckmark={true}
              />
            )}
          </StyledMenu>
          {modeSelection}
        </Box>
      </Box>
    </Box>
  );
};
